import { createApp } from 'vue'
import 'uno.css'
import 'virtual:uno.css'
import App from './App.vue'

import Router from '@/core/modules/Router'
import sentryPlugin from '@/core/modules/Sentry'
import pinia from '@/core/modules/Pinia'
import Column from 'primevue/column'
import ConfirmationService from 'primevue/confirmationservice'
import Select from 'primevue/select'
import MultiSelect from 'primevue/multiselect'
import SwipeVueConfig from '@/core/configs/SwipeVueConfig'
import Tooltip from 'primevue/tooltip'
import PrimeVue from 'primevue/config'
import { DialogService, ToastService } from 'primevue'
import clickAsync from '@/core/directives/ClickAsync'
import vPermission from '@/core/directives/Permission'
import posthogPlugin from '@/core/modules/PostHog'
import { VueQueryPlugin } from '@tanstack/vue-query'

/*
 * PrimeVue has no global default props. Without autoFilterFocus the trigger keeps focus while
 * typing on a closed select and every keystroke overwrites the filter value with that single
 * letter; with this default, focus jumps to the search field on open.
 * Only enabled with `filter`: MultiSelect otherwise focuses a search field that doesn't exist and crashes.
 * Vue calls a default factory with the raw props, where a bare `filter` attribute is still ''.
 */
type WithBaseProps = { extends?: { props?: Record<string, { default?: unknown }> } }
for (const component of [ Select, MultiSelect ] as WithBaseProps[]) {
  const autoFilterFocus = component.extends?.props?.autoFilterFocus

  if (autoFilterFocus) {
    autoFilterFocus.default = (props: { filter?: boolean | '' }) => props.filter !== undefined && props.filter !== false
  }
}

// use plugin
const app = createApp(App)

app.use(PrimeVue, SwipeVueConfig)
app.use(posthogPlugin)

app.use(pinia)
app.use(Router)
app.use(sentryPlugin, {
  router: app.config.globalProperties.$router,
})

app.use(ToastService)
app.use(DialogService)
app.use(ConfirmationService)

app.use(VueQueryPlugin, {
  enableDevtoolsV6Plugin: true,
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  },
})

app.component('Column', Column)

app.directive('tooltip', Tooltip)
app.directive('click-async', clickAsync)
app.directive('permission', vPermission)

app.mount('#app')
